import { useState } from 'react';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setFormStatus('sending');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === 'string') data.append(key, value);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d7bqciiqofe8vli40nhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-[#f7f8fa] py-28 md:py-36">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-16">
          <span className="text-slate-500 text-xs font-semibold tracking-[0.25em] uppercase">Contact</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-4">Get In Touch</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            We&apos;re here to help and answer any questions you might have.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Contact Info */}
          <div className="flex flex-col gap-6">
            {/* Address */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl">
                  <i className="ri-map-pin-line text-slate-600 text-lg" />
                </div>
                <h3 className="text-gray-900 font-semibold text-lg">Address</h3>
              </div>
              <div className="text-gray-600 text-sm leading-loose space-y-1">
                <p><span className="font-medium text-gray-800">Street:</span> 17 str. GENERAL KOLEV</p>
                <p><span className="font-medium text-gray-800">City:</span> Plovdiv</p>
                <p><span className="font-medium text-gray-800">Zip:</span> 4002</p>
                <p><span className="font-medium text-gray-800">Country:</span> Bulgaria</p>
              </div>
            </div>

            {/* Contact Details */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl">
                  <i className="ri-contacts-line text-slate-600 text-lg" />
                </div>
                <h3 className="text-gray-900 font-semibold text-lg">Contact Details</h3>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-slate-50 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-slate-500 text-base" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">Email</p>
                    <p className="text-gray-700 text-sm font-medium">help@vetonood.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-slate-50 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-slate-500 text-base" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">Phone</p>
                    <p className="text-gray-700 text-sm font-medium">+1 5756004826</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <h3 className="text-gray-900 font-semibold text-xl mb-6">Send Us a Message</h3>

            {formStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                  <i className="ri-check-line text-emerald-600 text-2xl" />
                </div>
                <p className="text-gray-900 font-semibold text-lg mb-2">Message Sent!</p>
                <p className="text-gray-500 text-sm">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
                <button
                  className="mt-6 text-slate-600 text-sm underline cursor-pointer"
                  onClick={() => setFormStatus('idle')}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Doe"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-slate-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-slate-400 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="How can we help?"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-slate-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Message <span className="normal-case text-gray-400">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us more about your inquiry..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-slate-400 transition-colors resize-none"
                  />
                  {charCount > 500 && (
                    <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                  )}
                </div>

                {formStatus === 'error' && (
                  <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending' || charCount > 500}
                  className="w-full bg-gray-900 text-white font-medium text-sm py-3.5 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer bar */}
        <div className="mt-16 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/2dd7c593-be74-4f63-b267-5ffd9c433bb3.png"
              alt="VETON OOD"
              className="h-7 w-auto object-contain"
            />
            <span className="text-gray-700 font-semibold text-sm">VETON OOD</span>
          </div>
          <p className="text-gray-400 text-sm text-center">
            &copy; {new Date().getFullYear()} VETON OOD. All rights reserved.
          </p>
          <p className="text-gray-400 text-sm">iOS Development Experts</p>
        </div>
      </div>
    </section>
  );
}
